import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { Spotlight } from './ui/spotlight';
import { Meteors } from './ui/meteors';

const inProgressTracks = [
  'Hospitality',
  'Retail & shops',
  'Service businesses',
  'Founders & startups',
  'Business automation',
  'Brand & graphic',
  'Apps & internal tools',
  'Booking & payments',
];

type ClientPortfolioLink =
  | { mode: 'inactive' }
  | { mode: 'comingSoon' }
  | { mode: 'external'; href: string };

type ClientLogoEntry =
  | { id: string; name: string; link: ClientPortfolioLink; kind: 'image'; src: string }
  | { id: string; name: string; link: ClientPortfolioLink; kind: 'emblem' }
  | { id: string; name: string; link: ClientPortfolioLink; kind: 'badge' };

const clientRoster: ClientLogoEntry[] = [
  {
    id: 'broll',
    name: 'Broll',
    kind: 'image',
    src: encodeURI('/Broll SVG.svg'),
    link: { mode: 'comingSoon' },
  },
  {
    id: 'calpharm',
    name: 'Calpharm',
    kind: 'image',
    src: encodeURI('/Calpharm SVG for signage.svg'),
    link: { mode: 'external', href: 'https://www.calpharm.co.za' },
  },
  {
    id: 'hicolour',
    name: 'HiColour',
    kind: 'image',
    src: encodeURI('/HiColourSVG.svg'),
    link: { mode: 'external', href: 'https://www.hicolour.co.za' },
  },
  {
    id: 'cnt',
    name: 'CNT Labs',
    kind: 'image',
    src: encodeURI('/CNT labs Modern WHITE only.svg'),
    link: { mode: 'external', href: 'https://cnt-labs.vercel.app' },
  },
  {
    id: 'countrypies',
    name: 'Country Pies',
    kind: 'image',
    src: encodeURI('/Country pies SVG.svg'),
    link: { mode: 'external', href: 'https://www.countrypies.co.za' },
  },
  {
    id: 'appletree',
    name: 'Apple Tree',
    kind: 'image',
    src: encodeURI('/AppleTree.svg'),
    link: { mode: 'inactive' },
  },
  { id: 'cigar', name: 'Cigar Society', kind: 'emblem', link: { mode: 'external', href: 'https://www.cigarsocietysa.com' } },
  {
    id: 'greenday',
    name: 'Greenday Property',
    kind: 'badge',
    link: { mode: 'external', href: 'https://www.normanraad.com' },
  },
];

const drawLine = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: (i: number) => ({
    pathLength: 1,
    opacity: 0.85,
    transition: {
      pathLength: { delay: i * 0.18, duration: 1.4, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] },
      opacity: { delay: i * 0.18, duration: 0.5 },
    },
  }),
};

const CigarSocietyEmblem: React.FC<{ accent: string }> = ({ accent }) => (
  <motion.svg
    viewBox="0 0 200 200"
    className="h-16 w-16 md:h-20 md:w-20"
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.4 }}
    style={{ overflow: 'visible' }}
  >
    <motion.circle cx="100" cy="100" r="90" fill="none" stroke={accent} strokeWidth="0.7" variants={drawLine} custom={0} />
    <motion.circle cx="100" cy="100" r="74" fill="none" stroke={accent} strokeWidth="0.5" variants={drawLine} custom={0.4} />
    <motion.path d="M100 30 L160 100 L100 170 L40 100 Z" fill="none" stroke={accent} strokeWidth="0.7" variants={drawLine} custom={0.7} />
    <motion.path d="M100 50 L145 100 L100 150 L55 100 Z" fill="none" stroke={accent} strokeWidth="0.5" variants={drawLine} custom={1} />
    <motion.line x1="32" y1="100" x2="168" y2="100" stroke={accent} strokeWidth="0.4" variants={drawLine} custom={1.3} />
    <motion.line x1="100" y1="32" x2="100" y2="168" stroke={accent} strokeWidth="0.4" variants={drawLine} custom={1.4} />
    <motion.path d="M88 85 C78 85, 72 92, 72 100 C72 108, 78 115, 88 115" fill="none" stroke={accent} strokeWidth="1.4" strokeLinecap="round" variants={drawLine} custom={1.7} />
    <motion.path d="M118 87 C112 85, 105 87, 105 93 C105 99, 118 99, 118 107 C118 113, 112 115, 105 113" fill="none" stroke={accent} strokeWidth="1.4" strokeLinecap="round" variants={drawLine} custom={1.95} />
  </motion.svg>
);

const GreendayBadge: React.FC<{ accent: string; textPrimary: string; textMuted: string }> = ({
  accent,
  textPrimary,
  textMuted,
}) => (
  <motion.div
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true, amount: 0.4 }}
    transition={{ duration: 0.55, ease: 'easeOut' }}
    className="flex flex-col items-center"
  >
    <span
      className="block text-[26px] md:text-[28px] leading-none"
      style={{
        fontFamily: '"Times New Roman", Georgia, serif',
        fontWeight: 300,
        letterSpacing: '0.018em',
        color: textPrimary,
      }}
    >
      Greenday
    </span>
    <motion.div
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.9, ease: 'easeOut', delay: 0.2 }}
      className="mt-2.5 h-px w-14 origin-center"
      style={{
        background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
        filter: `drop-shadow(0 0 5px ${accent})`,
      }}
    />
    <span
      className="mt-2.5 block text-[9px] font-medium uppercase"
      style={{
        color: textMuted,
        letterSpacing: '0.36em',
      }}
    >
      Property
    </span>
  </motion.div>
);

type ClientLogoChipProps = {
  client: ClientLogoEntry;
  delay: number;
  accent: string;
  cardBg: string;
  cardBorder: string;
  textPrimary: string;
  textMuted: string;
  isLight: boolean;
  onComingSoon: () => void;
};

const ClientLogoChip: React.FC<ClientLogoChipProps> = ({
  client,
  delay,
  accent,
  cardBg,
  cardBorder,
  textPrimary,
  textMuted,
  isLight,
  onComingSoon,
}) => {
  const { link } = client;
  const isClickable = link.mode !== 'inactive';

  const handleActivate = () => {
    if (link.mode === 'inactive') return;
    if (link.mode === 'comingSoon') {
      onComingSoon();
      return;
    }
    window.open(link.href, '_blank', 'noopener,noreferrer');
  };

  const ariaLabel =
    link.mode === 'inactive'
      ? undefined
      : link.mode === 'comingSoon'
        ? `${client.name}: case study coming soon`
        : `Visit ${client.name} website (opens in a new tab)`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 14, filter: 'blur(8px)' }}
      whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.55, ease: 'easeOut', delay }}
      whileHover="hover"
      className="group relative"
    >
      <div
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        aria-label={ariaLabel}
        onClick={isClickable ? handleActivate : undefined}
        onKeyDown={
          isClickable
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleActivate();
                }
              }
            : undefined
        }
        title={
          link.mode === 'external'
            ? 'Opens in a new tab'
            : link.mode === 'comingSoon'
              ? 'Coming soon'
              : undefined
        }
        className={`relative flex aspect-[4/3] items-center justify-center overflow-hidden rounded-2xl border p-5 transition-colors duration-300 ${
          isClickable ? 'cursor-pointer' : 'cursor-default'
        }`}
        style={{
          background: cardBg,
          borderColor: cardBorder,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
        }}
      >
        {client.kind === 'image' && (
          <img
            src={client.src}
            alt={`${client.name} client logo`}
            loading="lazy"
            decoding="async"
            className="max-h-[58%] w-auto max-w-[78%] select-none object-contain transition-all duration-300 group-hover:scale-[1.04]"
            style={{
              filter: isLight ? 'brightness(0)' : 'brightness(0) invert(1)',
              opacity: 0.88,
            }}
          />
        )}
        {client.kind === 'emblem' && <CigarSocietyEmblem accent={accent} />}
        {client.kind === 'badge' && (
          <GreendayBadge accent={accent} textPrimary={textPrimary} textMuted={textMuted} />
        )}

        <span
          className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap text-[8px] font-black uppercase opacity-0 transition-opacity duration-300 group-hover:opacity-90"
          style={{ color: accent, letterSpacing: '0.28em' }}
        >
          {client.name}
        </span>

        <svg
          className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
          aria-hidden="true"
          preserveAspectRatio="none"
        >
          <motion.rect
            x="0.75"
            y="0.75"
            width="calc(100% - 1.5px)"
            height="calc(100% - 1.5px)"
            rx="14"
            ry="14"
            fill="none"
            stroke={accent}
            strokeWidth={1.4}
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            variants={{
              hover: {
                pathLength: 1,
                opacity: 1,
                transition: {
                  pathLength: { duration: 0.65, ease: [0.32, 0.72, 0.4, 1] },
                  opacity: { duration: 0.15 },
                },
              },
            }}
            style={{
              filter: `drop-shadow(0 0 7px ${accent})`,
            }}
          />
        </svg>
      </div>
    </motion.div>
  );
};

type PillChipProps = {
  label: string;
  delay: number;
  accent: string;
  cardBg: string;
  cardBorder: string;
  textPrimary: string;
};

const PillChip: React.FC<PillChipProps> = ({ label, delay, accent, cardBg, cardBorder, textPrimary }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, filter: 'blur(6px)' }}
      whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, ease: 'easeOut', delay }}
      whileHover="hover"
      className="group relative inline-flex"
    >
      <div
        className="relative flex items-center gap-2 rounded-full border px-5 py-2.5 transition-colors duration-300"
        style={{
          background: cardBg,
          borderColor: cardBorder,
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
        }}
      >
        <span
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ background: accent, boxShadow: `0 0 8px ${accent}` }}
        />
        <span
          className="text-[12px] font-black uppercase leading-none"
          style={{
            color: textPrimary,
            letterSpacing: '0.18em',
          }}
        >
          {label}
        </span>

        <svg
          className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
          aria-hidden="true"
          preserveAspectRatio="none"
        >
          <motion.rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            rx="9999"
            ry="9999"
            fill="none"
            stroke={accent}
            strokeWidth={1.5}
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            variants={{
              hover: {
                pathLength: 1,
                opacity: 1,
                transition: {
                  pathLength: { duration: 0.55, ease: [0.32, 0.72, 0.4, 1] },
                  opacity: { duration: 0.15 },
                },
              },
            }}
            style={{
              filter: `drop-shadow(0 0 6px ${accent})`,
            }}
          />
        </svg>
      </div>
    </motion.div>
  );
};

const PortfolioPage: React.FC = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [comingSoonOpen, setComingSoonOpen] = useState(false);

  useEffect(() => {
    if (!comingSoonOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setComingSoonOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [comingSoonOpen]);

  const accent = isLight ? '#0f766e' : '#f97316';
  const surface = isLight ? '#f1f5f9' : '#050505';
  const cardBg = isLight ? 'rgba(255,255,255,0.78)' : 'rgba(10,10,10,0.55)';
  const cardBorder = isLight ? 'rgba(15,118,110,0.18)' : 'rgba(251,146,60,0.18)';
  const textPrimary = isLight ? '#06131a' : '#fff7ed';
  const textMuted = isLight ? 'rgba(8,47,73,0.72)' : 'rgba(254,215,170,0.72)';

  return (
    <main
      className="relative min-h-screen overflow-hidden"
      style={{ background: surface, color: textPrimary, letterSpacing: '0.012em' }}
    >
      <Meteors number={18} theme={theme} />

      <div className="relative">
        <Spotlight
          className="-top-40 left-0 md:-top-20 md:left-60"
          fill={isLight ? 'rgba(20,184,166,0.55)' : 'rgba(251,146,60,0.55)'}
        />

        <section className="relative z-10 mx-auto flex min-h-[88vh] max-w-6xl flex-col items-center justify-center px-6 pt-32 pb-20 text-center">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="text-[10px] font-black uppercase tracking-[0.32em]"
            style={{ color: accent }}
          >
            Portfolio · In motion
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 18, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.05 }}
            className="mt-6 text-5xl font-black leading-[1.1] md:text-7xl md:leading-[1.08]"
            style={{ letterSpacing: '-0.005em' }}
          >
            The portfolio is{' '}
            <span style={{ color: accent }}>evolving</span>
            <span style={{ color: accent }}>.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.18 }}
            className="mt-7 max-w-2xl text-[15px] font-medium leading-[1.85] md:text-lg md:leading-[1.85]"
            style={{ color: textMuted, letterSpacing: '0.018em' }}
          >
            DesignDot
            <span style={{ color: accent }}>Dev</span> has been busy. We're shipping faster than we can showcase right now —
            new builds for restaurants, retailers, service operators, and founders are launching every few weeks.
            A proper, curated case-study experience is being crafted alongside our payment and onboarding flow,
            and it'll go live the moment it's worthy of the work.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.32 }}
            className="mt-10 w-full max-w-6xl"
          >
            <p
              className="text-center text-[11px] font-black uppercase tracking-[0.32em]"
              style={{ color: accent }}
            >
              A few we've built for
            </p>
            <p
              className="mx-auto mt-4 max-w-xl text-center text-[13px] font-medium leading-[1.7]"
              style={{ color: textMuted, letterSpacing: '0.02em' }}
            >
              Selected clients across property, pharma, print, biotech, lifestyle and commercial real estate.
            </p>

            <div className="mt-8 grid w-full grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-6">
              {clientRoster.map((client, index) => (
                <ClientLogoChip
                  key={client.id}
                  client={client}
                  delay={index * 0.07}
                  accent={accent}
                  cardBg={cardBg}
                  cardBorder={cardBorder}
                  textPrimary={textPrimary}
                  textMuted={textMuted}
                  isLight={isLight}
                  onComingSoon={() => setComingSoonOpen(true)}
                />
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.45 }}
            className="mt-16 w-full max-w-4xl"
          >
            <p
              className="text-center text-[11px] font-black uppercase tracking-[0.28em]"
              style={{ color: accent }}
            >
              Currently shipping for
            </p>

            <div className="mt-7 flex flex-wrap items-center justify-center gap-3 md:gap-3.5">
              {inProgressTracks.map((label, index) => (
                <PillChip
                  key={label}
                  label={label}
                  delay={index * 0.05}
                  accent={accent}
                  cardBg={cardBg}
                  cardBorder={cardBorder}
                  textPrimary={textPrimary}
                />
              ))}
            </div>

            <p
              className="mx-auto mt-14 max-w-2xl text-center text-[14px] font-medium leading-[1.75]"
              style={{ color: textMuted, letterSpacing: '0.018em' }}
            >
              Want examples relevant to your industry? Reach out and we'll send live work
              and references that match what you're trying to ship.
            </p>
          </motion.div>
        </section>
      </div>

      {comingSoonOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="portfolio-coming-soon-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/55 backdrop-blur-sm"
            aria-label="Close dialog"
            onClick={() => setComingSoonOpen(false)}
          />
          <div
            className={`relative z-10 w-full max-w-md rounded-2xl border px-7 py-8 text-center shadow-2xl ${
              isLight
                ? 'border-teal-400/35 bg-white/95 text-teal-950'
                : 'border-orange-300/30 bg-[#0a0a0a]/95 text-orange-50'
            }`}
            style={{ backdropFilter: 'blur(16px)' }}
          >
            <p
              id="portfolio-coming-soon-title"
              className="text-[11px] font-black uppercase tracking-[0.28em]"
              style={{ color: accent }}
            >
              Coming soon
            </p>
            <p className="mt-5 text-[15px] font-medium leading-relaxed" style={{ color: textMuted }}>
              The Broll case study and live site preview are not public yet. We're finishing the showcase — check back
              soon.
            </p>
            <button
              type="button"
              onClick={() => setComingSoonOpen(false)}
              className="mt-8 rounded-full px-8 py-3 text-xs font-black uppercase tracking-[0.16em] transition-opacity hover:opacity-90"
              style={{
                background: accent,
                color: isLight ? '#ffffff' : '#0a0a0a',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default PortfolioPage;
